import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { newsList, type NewsCategory, type NewsItem } from "@/mocks/news";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PAGE_SIZE = 5;

const categories: { key: NewsCategory | "全部"; label: string }[] = [
  { key: "全部", label: "全部资讯" },
  { key: "电影新闻", label: "电影新闻" },
  { key: "电视剧新闻", label: "电视剧新闻" },
  { key: "综合资讯", label: "综合资讯" },
];

export default function News() {
  const [category, setCategory] = useState<NewsCategory | "全部">("全部");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [active, setActive] = useState<NewsItem | null>(null);

  const filtered = useMemo(() => {
    const list = category === "全部" ? newsList : newsList.filter((n) => n.category === category);
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [category]);

  const shown = filtered.slice(0, visible);

  const changeCategory = (c: NewsCategory | "全部") => {
    setCategory(c);
    setVisible(PAGE_SIZE);
  };

  useDocumentTitle("影视资讯 - 诱人的飞行");

  return (
    <div className="w-full">
      <div className="w-full bg-primary-900 py-8 md:py-12 px-4 md:px-8">
        <nav className="text-xs text-background-300 mb-3 flex items-center gap-1.5">
          <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-background-100">影视资讯</span>
        </nav>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-2">影视资讯</h1>
        <p className="text-sm text-background-300 max-w-2xl">最新影视动态、剧集消息与行业观察，帮你跟上这个圈子的节奏。</p>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-10">
        {/* 分类筛选 */}
        <div className="inline-flex items-center px-1 py-1 rounded-full bg-background-100 border border-background-200 mb-7 flex-wrap">
          {categories.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => changeCategory(c.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                category === c.key ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-background-300 rounded-lg">
            <i className="ri-newspaper-line text-4xl text-foreground-400 mb-4 inline-block" aria-hidden="true"></i>
            <p className="text-foreground-700">该分类下暂无资讯内容</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {shown.map((n, i) => (
                <article
                  key={n.id}
                  className="p-5 md:p-6 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-900 text-xs">{n.category}</span>
                    <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                      <i className="ri-calendar-line" aria-hidden="true"></i>
                      {n.date}
                    </span>
                  </div>
                  <h2 className="text-base md:text-lg font-medium text-foreground-950 mb-2 leading-snug">
                    <button
                      type="button"
                      onClick={() => setActive(n)}
                      className="text-left hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      {n.title}
                    </button>
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{n.summary}</p>
                  <button
                    type="button"
                    onClick={() => setActive(n)}
                    className="text-sm text-primary-600 hover:text-accent-600 transition-colors flex items-center gap-1 cursor-pointer"
                  >
                    阅读全文
                    <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                  </button>
                </article>
              ))}
            </div>

            {visible < filtered.length && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="px-6 py-2.5 rounded-md bg-background-100 border border-background-200 hover:border-primary-300 text-sm font-medium text-foreground-800 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-arrow-down-line" aria-hidden="true"></i>
                  加载更多
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 资讯详情弹层 */}
      {active && (
        <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-foreground-950/50 cursor-pointer"
            onClick={() => setActive(null)}
            aria-label="关闭资讯详情"
          ></button>
          <div className="relative w-full md:max-w-2xl max-h-[85vh] overflow-y-auto bg-background-50 md:rounded-lg rounded-t-2xl border border-background-200 animate-fade-up">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 md:px-7 py-4 flex items-center justify-between">
              <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-900 text-xs">{active.category}</span>
              <button
                type="button"
                onClick={() => setActive(null)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>
            <div className="px-5 md:px-7 py-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 leading-snug">{active.title}</h2>
              <p className="text-xs text-foreground-500 mb-6 flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {active.date}
              </p>
              <div className="space-y-4">
                {active.content.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">{p}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}